/**
 * Clinical safety checks for prescribing & dispensing.
 * Mirrors the allergy / interaction gates used by top HMS (Promed, Medical365, Epic MedAlerts).
 * Built-in common Indian formulary pairs — extend via hospital settings later.
 */

/** Known clinically significant interactions (drug A ↔ drug B). Keys are lowercase name fragments. */
const INTERACTION_PAIRS = [
  { a: 'warfarin', b: 'aspirin', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'warfarin', b: 'ibuprofen', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'warfarin', b: 'diclofenac', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'metformin', b: 'contrast', severity: 'high', note: 'Lactic acidosis risk with iodinated contrast' },
  { a: 'simvastatin', b: 'clarithromycin', severity: 'high', note: 'Myopathy / rhabdomyolysis risk' },
  { a: 'simvastatin', b: 'erythromycin', severity: 'high', note: 'Myopathy / rhabdomyolysis risk' },
  { a: 'atorvastatin', b: 'clarithromycin', severity: 'moderate', note: 'Increased statin levels' },
  { a: 'digoxin', b: 'amiodarone', severity: 'high', note: 'Digoxin toxicity — reduce digoxin dose' },
  { a: 'digoxin', b: 'verapamil', severity: 'high', note: 'Digoxin toxicity risk' },
  { a: 'ssri', b: 'tramadol', severity: 'high', note: 'Serotonin syndrome risk' },
  { a: 'fluoxetine', b: 'tramadol', severity: 'high', note: 'Serotonin syndrome risk' },
  { a: 'sertraline', b: 'tramadol', severity: 'high', note: 'Serotonin syndrome risk' },
  { a: 'maoi', b: 'ssri', severity: 'high', note: 'Serotonin syndrome — contraindicated' },
  { a: 'ace inhibitor', b: 'potassium', severity: 'moderate', note: 'Hyperkalemia risk' },
  { a: 'enalapril', b: 'spironolactone', severity: 'moderate', note: 'Hyperkalemia risk' },
  { a: 'ramipril', b: 'spironolactone', severity: 'moderate', note: 'Hyperkalemia risk' },
  { a: 'methotrexate', b: 'nsaid', severity: 'high', note: 'Methotrexate toxicity' },
  { a: 'methotrexate', b: 'ibuprofen', severity: 'high', note: 'Methotrexate toxicity' },
  { a: 'ciprofloxacin', b: 'theophylline', severity: 'moderate', note: 'Theophylline toxicity' },
  { a: 'ciprofloxacin', b: 'tizanidine', severity: 'high', note: 'Severe hypotension — contraindicated' },
  { a: 'sildenafil', b: 'nitrate', severity: 'high', note: 'Severe hypotension — contraindicated' },
  { a: 'sildenafil', b: 'isosorbide', severity: 'high', note: 'Severe hypotension — contraindicated' },
  { a: 'clopidogrel', b: 'omeprazole', severity: 'moderate', note: 'Reduced antiplatelet effect' },
  { a: 'lithium', b: 'nsaid', severity: 'high', note: 'Lithium toxicity' },
  { a: 'lithium', b: 'ibuprofen', severity: 'high', note: 'Lithium toxicity' },
  { a: 'azithromycin', b: 'amiodarone', severity: 'moderate', note: 'QT prolongation' },
  { a: 'ondansetron', b: 'amiodarone', severity: 'moderate', note: 'QT prolongation' },
  { a: 'insulin', b: 'propranolol', severity: 'moderate', note: 'Masks hypoglycemia symptoms' },
  { a: 'glimepiride', b: 'aspirin', severity: 'moderate', note: 'Enhanced hypoglycemic effect' },
  { a: 'carbamazepine', b: 'oral contraceptive', severity: 'moderate', note: 'Reduced contraceptive efficacy' },
  { a: 'rifampicin', b: 'oral contraceptive', severity: 'high', note: 'Contraceptive failure risk' },
  { a: 'allopurinol', b: 'azathioprine', severity: 'high', note: 'Bone marrow suppression' },
  { a: 'verapamil', b: 'beta blocker', severity: 'high', note: 'Bradycardia / heart block' },
  { a: 'verapamil', b: 'atenolol', severity: 'high', note: 'Bradycardia / heart block' },
  { a: 'verapamil', b: 'metoprolol', severity: 'high', note: 'Bradycardia / heart block' },
  // Expanded Indian formulary pairs (still curated — not a full 1.7L commercial DB)
  { a: 'paracetamol', b: 'warfarin', severity: 'moderate', note: 'High-dose paracetamol may increase INR' },
  { a: 'acetaminophen', b: 'warfarin', severity: 'moderate', note: 'High-dose paracetamol may increase INR' },
  { a: 'amiodarone', b: 'warfarin', severity: 'high', note: 'Marked INR increase — reduce warfarin' },
  { a: 'fluconazole', b: 'warfarin', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'metronidazole', b: 'warfarin', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'trimethoprim', b: 'warfarin', severity: 'moderate', note: 'Increased INR' },
  { a: 'cotrimoxazole', b: 'warfarin', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'spironolactone', b: 'potassium', severity: 'high', note: 'Severe hyperkalemia risk' },
  { a: 'losartan', b: 'spironolactone', severity: 'moderate', note: 'Hyperkalemia risk' },
  { a: 'telmisartan', b: 'spironolactone', severity: 'moderate', note: 'Hyperkalemia risk' },
  { a: 'amlodipine', b: 'simvastatin', severity: 'moderate', note: 'Limit simvastatin dose with amlodipine' },
  { a: 'diltiazem', b: 'simvastatin', severity: 'moderate', note: 'Increased statin exposure' },
  { a: 'clarithromycin', b: 'colchicine', severity: 'high', note: 'Colchicine toxicity — avoid' },
  { a: 'erythromycin', b: 'colchicine', severity: 'high', note: 'Colchicine toxicity — avoid' },
  { a: 'linezolid', b: 'ssri', severity: 'high', note: 'Serotonin syndrome risk' },
  { a: 'linezolid', b: 'tramadol', severity: 'high', note: 'Serotonin syndrome risk' },
  { a: 'tramadol', b: 'maoi', severity: 'high', note: 'Seizure / serotonin risk — contraindicated' },
  { a: 'theophylline', b: 'ciprofloxacin', severity: 'moderate', note: 'Theophylline toxicity' },
  { a: 'phenytoin', b: 'fluconazole', severity: 'moderate', note: 'Increased phenytoin levels' },
  { a: 'carbamazepine', b: 'clarithromycin', severity: 'moderate', note: 'Increased carbamazepine levels' },
  { a: 'isoniazid', b: 'paracetamol', severity: 'moderate', note: 'Hepatotoxicity risk with high doses' },
  { a: 'rifampicin', b: 'warfarin', severity: 'high', note: 'Reduced warfarin effect' },
  { a: 'rifampicin', b: 'metformin', severity: 'moderate', note: 'May alter glycemic control' },
  { a: 'levothyroxine', b: 'calcium', severity: 'moderate', note: 'Reduced thyroxine absorption — separate doses' },
  { a: 'levothyroxine', b: 'iron', severity: 'moderate', note: 'Reduced thyroxine absorption — separate doses' },
  { a: 'levofloxacin', b: 'amiodarone', severity: 'moderate', note: 'QT prolongation' },
  { a: 'moxifloxacin', b: 'amiodarone', severity: 'high', note: 'QT prolongation' },
  { a: 'domperidone', b: 'erythromycin', severity: 'high', note: 'QT prolongation — avoid' },
  { a: 'ketorolac', b: 'aspirin', severity: 'high', note: 'Increased GI bleed risk' },
  { a: 'aceclofenac', b: 'warfarin', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'pioglitazone', b: 'insulin', severity: 'moderate', note: 'Fluid retention / heart failure risk' },
  { a: 'sitagliptin', b: 'insulin', severity: 'moderate', note: 'Hypoglycemia risk — dose adjust' },
  { a: 'clopidogrel', b: 'esomeprazole', severity: 'moderate', note: 'Reduced antiplatelet effect' },
  { a: 'dabigatran', b: 'ketoconazole', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'apixaban', b: 'ketoconazole', severity: 'high', note: 'Increased bleeding risk' },
  { a: 'methotrexate', b: 'trimethoprim', severity: 'high', note: 'Bone marrow toxicity' },
  { a: 'azathioprine', b: 'allopurinol', severity: 'high', note: 'Bone marrow suppression' },
  { a: 'gentamicin', b: 'furosemide', severity: 'moderate', note: 'Ototoxicity / nephrotoxicity' },
  { a: 'amikacin', b: 'vancomycin', severity: 'moderate', note: 'Nephrotoxicity risk' },
  { a: 'cisplatin', b: 'aminoglycoside', severity: 'high', note: 'Severe nephrotoxicity' },
  { a: 'theophylline', b: 'propranolol', severity: 'moderate', note: 'Reduced theophylline clearance' },
  { a: 'digoxin', b: 'clarithromycin', severity: 'high', note: 'Digoxin toxicity risk' },
  { a: 'digoxin', b: 'spironolactone', severity: 'moderate', note: 'May alter digoxin levels / hyperkalemia' },
];

/** Allergy keyword → drug fragments that should trigger an alert */
const ALLERGY_MAP = [
  { allergy: 'penicillin', drugs: ['penicillin', 'amoxicillin', 'ampicillin', 'augmentin', 'cloxacillin', 'piperacillin'] },
  { allergy: 'amoxicillin', drugs: ['amoxicillin', 'augmentin', 'ampicillin', 'penicillin'] },
  { allergy: 'sulfa', drugs: ['sulfamethoxazole', 'cotrimoxazole', 'bactrim', 'septran', 'sulfonamide'] },
  { allergy: 'sulphonamide', drugs: ['sulfamethoxazole', 'cotrimoxazole', 'bactrim', 'septran'] },
  { allergy: 'aspirin', drugs: ['aspirin', 'acetylsalicylic', 'ecosprin'] },
  { allergy: 'nsaid', drugs: ['ibuprofen', 'diclofenac', 'naproxen', 'mefenamic', 'aceclofenac', 'ketorolac'] },
  { allergy: 'ibuprofen', drugs: ['ibuprofen', 'brufen'] },
  { allergy: 'diclofenac', drugs: ['diclofenac', 'voveran'] },
  { allergy: 'cephalosporin', drugs: ['cefixixin', 'ceftriaxone', 'cefixxime', 'cefpodoxime', 'cephalexin', 'cefazolin'] },
  { allergy: 'quinolone', drugs: ['ciprofloxacin', 'levofloxacin', 'ofloxacin', 'norfloxacin', 'moxifloxacin'] },
  { allergy: 'fluoroquinolone', drugs: ['ciprofloxacin', 'levofloxacin', 'ofloxacin', 'norfloxacin'] },
  { allergy: 'iodine', drugs: ['contrast', 'iohexol', 'povidone'] },
  { allergy: 'latex', drugs: [] },
  { allergy: 'paracetamol', drugs: ['paracetamol', 'acetaminophen', 'crocin', 'dolo'] },
  { allergy: 'morphine', drugs: ['morphine', 'tramadol', 'codeine', 'fentanyl', 'pethidine'] },
  { allergy: 'opioid', drugs: ['morphine', 'tramadol', 'codeine', 'fentanyl', 'oxycodone'] },
];

function norm(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function drugMatches(fragment, drugText) {
  if (!fragment || !drugText) return false;
  return drugText.includes(fragment) || fragment.includes(drugText);
}

/**
 * Check patient allergies against a list of drug names.
 * @returns {{ severity, allergy, drug, note }[]}
 */
export function checkAllergyAlerts(allergiesText, drugNames = []) {
  const allergyParts = String(allergiesText || '')
    .split(/[,;/|]+/)
    .map((s) => norm(s))
    .filter(Boolean);
  if (!allergyParts.length || !drugNames.length) return [];

  const alerts = [];
  for (const allergy of allergyParts) {
    for (const map of ALLERGY_MAP) {
      if (!allergy.includes(map.allergy) && !map.allergy.includes(allergy)) continue;
      for (const drug of drugNames) {
        const d = norm(drug);
        if (map.drugs.some((frag) => drugMatches(frag, d)) || drugMatches(map.allergy, d)) {
          alerts.push({
            severity: 'high',
            allergy,
            drug,
            note: `Patient allergy to "${allergy}" may cross-react with ${drug}`,
          });
        }
      }
    }
    // Direct name match (e.g. allergy "Augmentin" vs drug "Augmentin 625")
    for (const drug of drugNames) {
      const d = norm(drug);
      if (d.includes(allergy) || allergy.includes(d.split(' ')[0])) {
        if (!alerts.some((a) => a.drug === drug && a.allergy === allergy)) {
          alerts.push({
            severity: 'high',
            allergy,
            drug,
            note: `Patient listed allergy matches ${drug}`,
          });
        }
      }
    }
  }
  return alerts;
}

/**
 * Check pairwise drug–drug interactions in a prescription.
 * @returns {{ severity, drugA, drugB, note }[]}
 */
export function checkDrugInteractions(drugNames = []) {
  const norms = drugNames.map((d) => ({ raw: d, n: norm(d) })).filter((x) => x.n);
  const hits = [];
  for (let i = 0; i < norms.length; i += 1) {
    for (let j = i + 1; j < norms.length; j += 1) {
      for (const pair of INTERACTION_PAIRS) {
        const matchAB = drugMatches(pair.a, norms[i].n) && drugMatches(pair.b, norms[j].n);
        const matchBA = drugMatches(pair.b, norms[i].n) && drugMatches(pair.a, norms[j].n);
        if (matchAB || matchBA) {
          hits.push({
            severity: pair.severity,
            drugA: norms[i].raw,
            drugB: norms[j].raw,
            note: pair.note,
          });
        }
      }
    }
  }
  return hits;
}

/**
 * Combined safety check for EMR save / pharmacy dispense.
 * @returns {{ ok: boolean, blocking: boolean, allergies: [], interactions: [], summary: string }}
 */
export function runSafetyCheck({ allergies = '', drugs = [] } = {}) {
  const allergyAlerts = checkAllergyAlerts(allergies, drugs);
  const interactions = checkDrugInteractions(drugs);
  const blocking = allergyAlerts.some((a) => a.severity === 'high')
    || interactions.some((i) => i.severity === 'high');
  const parts = [];
  if (allergyAlerts.length) parts.push(`${allergyAlerts.length} allergy alert(s)`);
  if (interactions.length) parts.push(`${interactions.length} interaction(s)`);
  return {
    ok: allergyAlerts.length === 0 && interactions.length === 0,
    blocking,
    allergies: allergyAlerts,
    interactions,
    summary: parts.length ? parts.join(', ') : 'No safety alerts',
  };
}

/**
 * Suggest FEFO batch from inventory items that share the same name/generic.
 * Prefers earliest expiry with stock > 0.
 */
export function suggestFefoBatch(item, inventory = []) {
  if (!item) return null;
  const name = norm(item.name);
  const gen = norm(item.saltComposition || item.genericName);
  const candidates = (inventory || [])
    .filter((i) => i.active !== false && Number(i.stockQty) > 0)
    .filter((i) => {
      const n = norm(i.name);
      const g = norm(i.saltComposition || i.genericName);
      return n === name || (gen && g === gen) || i.itemId === item.itemId;
    })
    .map((i) => ({
      ...i,
      expiryTs: i.expiry ? new Date(i.expiry).getTime() : Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.expiryTs - b.expiryTs);
  return candidates[0] || item;
}

/**
 * Suggest substitutes when stock is zero — same generic, or same category.
 */
export function suggestSubstitutes(drugName, inventory = [], limit = 5) {
  const q = norm(drugName);
  if (!q) return [];
  const active = (inventory || []).filter((i) => i.active !== false && Number(i.stockQty) > 0);
  const exact = active.find((i) => norm(i.name) === q);
  if (exact) return [];
  const byGeneric = active.filter((i) => {
    const g = norm(i.saltComposition || i.genericName);
    const brand = norm(i.brandName);
    return (g && (q.includes(g) || g.includes(q.split(' ')[0])))
      || (brand && (q.includes(brand) || brand.includes(q.split(' ')[0])));
  });
  if (byGeneric.length) return byGeneric.slice(0, limit);
  // Fuzzy name contains
  return active
    .filter((i) => {
      const n = norm(i.name);
      return n.includes(q.split(' ')[0]) || q.includes(n.split(' ')[0]);
    })
    .slice(0, limit);
}
